import {Component, DOCUMENT, Inject, PLATFORM_ID, ChangeDetectionStrategy} from '@angular/core';
import {Meta, Title} from "@angular/platform-browser";
import {TranslateService, TranslationObject} from "@ngx-translate/core";
import {NavigationStart, Router} from "@angular/router";
import {filter} from "rxjs";

import {i18n} from "../../helpers/i18n";
import {environment} from "../../environments/environment";
import {ThemeService} from "../services/theme.service";
import {AdClickService} from "../services/ad.click.service";

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent {

    private pagesForNotIndexation = ['/chat', '/login', '/auth/registration', '/auth/registration/confirm', '/auth/restore', '/auth/restore/finish',]

    private titles = {}

    private lastRoute = '/'

    constructor(private themeService: ThemeService, private translate: TranslateService, private title: Title, private meta: Meta, private router: Router, private adClickService: AdClickService, @Inject(PLATFORM_ID) private platformId: any, @Inject(DOCUMENT) private readonly document: Document,) {
        // Идентификатор клика по рекламе есть в URL только на первой странице, до навигации роутера
        this.adClickService.capture()

        router.events.pipe(
            filter((event) => event instanceof NavigationStart)
        ).subscribe((event: NavigationStart) => {
            this.lastRoute = event.url
            this.updateSeoTags(event.url)
            this.updateTitle(event.url)
        });

        this.themeService.init();


        this.translate.setTranslation(environment.language, environment.translations as TranslationObject);

        // Set the default language - translations are already available
        this.translate.setFallbackLang(environment.language);
        this.translate.use(environment.language);

        this.setDocumentLanguage()

        translate.get(i18n.chat_long_title).subscribe(text => {
            this.prepareTitles()
            this.updateTitle(this.lastRoute)
        })

        translate.get(i18n.site_description).subscribe(text => {
            meta.updateTag({
                name: 'description',
                content: text
            })
        })

        translate.get(i18n.site_keywords).subscribe(text => {
            meta.updateTag({
                name: 'keywords',
                content: text
            })
        })

        this.createOpenGraphTags(this.lastRoute)

        this.injectGlobalStructuredData()
    }

    private prepareTitles() {
        this.titles = {
            '/': this.translate.instant(i18n.chat_long_title),
            '/login': `${this.translate.instant(i18n.login_title)} | ${this.translate.instant(i18n.chat_title)}`,
            '/auth/registration': `${this.translate.instant(i18n.registration)} | ${this.translate.instant(i18n.chat_title)}`,
            '/auth/registration/confirm': `${this.translate.instant(i18n.email_confirmation)} | ${this.translate.instant(i18n.chat_title)}`,
            '/auth/restore': `${this.translate.instant(i18n.access_restoration_step_1)} | ${this.translate.instant(i18n.chat_title)}`,
            '/auth/restore/finish': `${this.translate.instant(i18n.access_restoration_step_2)} | ${this.translate.instant(i18n.chat_title)}`,
            '/rules': `${this.translate.instant(i18n.rules_title)} | ${this.translate.instant(i18n.chat_title)}`,
            '/privacy_policy': `${this.translate.instant(i18n.privacy_policy)} | ${this.translate.instant(i18n.chat_title)}`,
            '/contacts': `${this.translate.instant(i18n.contacts)} | ${this.translate.instant(i18n.chat_title)}`,
            '/qa': `${this.translate.instant(i18n.qa)} | ${this.translate.instant(i18n.chat_title)}`,
            '/blog': `${this.translate.instant(i18n.blog_title)} | ${this.translate.instant(i18n.chat_title)}`,
        }
    }

    // Язык страницы объявляется в <html lang>. index.html общий для SX/VC-сборок,
    // поэтому значение проставляется рантаймом и попадает в пререндеренный HTML.
    private setDocumentLanguage() {
        this.document.documentElement.lang = environment.language
    }

    private updateSeoTags(path: string) {
        this.removeAlternateAndCanonicalLinkElement()

        // Взаимный hreflang между EN- и RU-версиями сети (только в <head>, без видимых UI-ссылок).
        // Реальные разные URL: en → 18sexchat, ru → virt-chat, x-default → EN-версия.
        //
        // Соответствие доменов существует ТОЛЬКО для главной: у RU-версии нет аналогов
        // ни лендингов, ни статей блога (vc-sitemap.xml — это лишь /, /rules, /privacy_policy).
        // Проставлять alternate на внутренних страницах нельзя: страница объявляла бы своим
        // языковым аналогом чужую главную, что противоречит её же canonical.
        if (this.isHomePage(path)) {
            this.createAlternateLinkElement('default', `https://${environment.enDomain}/`)
            this.createAlternateLinkElement('en', `https://${environment.enDomain}/`)
            this.createAlternateLinkElement('ru', `https://${environment.ruDomain}/`)
        }

        this.createCanonicalLink(path)

        if (this.pagesForNotIndexation.indexOf(this.pathWithoutParams(path)) > -1) {
            this.meta.updateTag({
                name: 'robots',
                content: 'noindex'
            })
        } else {
            this.meta.removeTag("name='robots'")
        }

        if (!path.includes('/blog/')) {
            this.translate.get(i18n.site_description).subscribe(text => {
                this.meta.updateTag({
                    name: 'description',
                    content: text
                })
            })
        }
    }

    private removeAlternateAndCanonicalLinkElement() {
        const links = this.document.head.getElementsByTagName('link')

        let i = 0
        while (i < links.length) {
            //Если элемент с массива удаляется, то длинна массива тоже уменьшается
            //в связи с этим после удаления нужно еще раз проверить тот же индекс
            if (links[i].hasAttribute('rel')
                && (links[i].getAttribute('rel') === 'alternate'
                    || links[i].getAttribute('rel') === 'canonical')) {
                links[i].remove()
            } else {
                i++
            }
        }
    }

    // '/' может прийти с query-параметрами (например, идентификатор рекламного клика),
    // поэтому сравниваем только путь.
    private isHomePage(path: string): boolean {
        return this.pathWithoutParams(path) === '/'
    }

    private pathWithoutParams(path: string): string {
        return path.split('?')[0].split('#')[0]
    }

    private createAlternateLinkElement(lang: string, href: string) {
        let langParam = lang

        if (lang === 'default') {
            langParam = 'x-default'
        }

        const link = this.document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', langParam);
        link.setAttribute('href', href);
        this.document.head.appendChild(link)
    }

    // path приходит из NavigationStart.url, то есть вместе с query и фрагментом.
    // Заход по рекламной ссылке (?adclick=...) не должен порождать отдельный canonical:
    // иначе одна и та же страница дробится на варианты и конкурирует сама с собой.
    private createCanonicalLink(path: string) {
        const link = this.document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', `https://${environment.canonicalDomain}${this.pathWithoutParams(path)}`);
        this.document.head.appendChild(link)
    }

    private createOpenGraphTags(path: string,) {
        let link = this.document.createElement('meta')
        link.setAttribute('property', 'og:title')
        link.setAttribute('content', this.translate.instant(i18n.chat_title))
        this.document.head.appendChild(link)

        link = this.document.createElement('meta')
        link.setAttribute('property', 'og:description')
        link.setAttribute('content', this.translate.instant(i18n.site_description))
        this.document.head.appendChild(link)

        /*link = this.document.createElement('meta')
        link.setAttribute('property', 'og:image')
        link.setAttribute('content', title)*/

        link = this.document.createElement('meta')
        link.setAttribute('property', 'og:url')
        link.setAttribute('content', 'https://' + environment.canonicalDomain)
        this.document.head.appendChild(link)

        link = this.document.createElement('meta')
        link.setAttribute('property', 'og:type')
        link.setAttribute('content', 'website')
        this.document.head.appendChild(link)

        link = this.document.createElement('meta')
        link.setAttribute('property', 'og:site_name')
        link.setAttribute('content', environment.name)
        this.document.head.appendChild(link)

    }

    private injectGlobalStructuredData() {
        const domain = `https://${environment.canonicalDomain}`

        const organization = {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: environment.name,
            url: `${domain}/`,
            logo: `${domain}/icon.svg`,
        }

        const website = {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: environment.name,
            url: `${domain}/`,
            potentialAction: {
                '@type': 'SearchAction',
                target: `${domain}/blog?q={search_term_string}`,
                'query-input': 'required name=search_term_string',
            },
        }

        this.appendJsonLd(organization, 'ld-organization')
        this.appendJsonLd(website, 'ld-website')
    }

    private appendJsonLd(data: object, id: string) {
        const existing = this.document.getElementById(id)
        if (existing) {
            existing.remove()
        }

        const script = this.document.createElement('script')
        script.setAttribute('type', 'application/ld+json')
        script.setAttribute('id', id)
        script.textContent = JSON.stringify(data)
        this.document.head.appendChild(script)
    }

    private updateTitle(path: string) {
        let title = this.titles[path]

        if (!title) {
            title = this.translate.instant(i18n.chat_title)
        }

        this.title.setTitle(title)

    }
}
